import React from 'react'
import {
  Code,
  CodeBlock,
  Link,
  List,
  Heading,
  PageHeading,
  Text,
} from '../../../shared/typography'
import { StackView } from '@planningcenter/tapestry-react'

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Add-ons API Reference</PageHeading>

      <Text>
        Planning Center add-ons execute in a Web Worker, specifically in a{' '}
        <Link
          to="https://developer.mozilla.org/en-US/docs/Web/API/DedicatedWorkerGlobalScope"
          external
        >
          <Code>DedicatedWorkerGlobalScope</Code>
        </Link>{' '}
        context. Inside your add-on, <Code>self</Code> refers to this global
        scope. It is similar to the <Code>window</Code> object in the browser's
        main thread, but with different properties and methods.
      </Text>

      <Text>
        We make four additional functions available to you through that global
        scope:
      </Text>

      <Heading anchored id="authenticated-fetch" size={1} level={2}>
        <Code>authenticatedFetch</Code>
      </Heading>

      <Text>
        This function can be used to make authenticated API calls to the
        Planning Center API. It works much like{' '}
        <Link
          to="https://developer.mozilla.org/en-US/docs/Web/API/Window/fetch"
          external
        >
          <Code>fetch()</Code>
        </Link>{' '}
        but handles API authorization and JSON deserialization for you.
      </Text>

      <CodeBlock language="javascript">
        {`
authenticatedFetch("/people/v2/me")
  .then(({ data }) => {
    setPerson(data.attributes.first_name)
  })
  .catch((err) => {
    console.error(err)
    setPerson("error fetching person")
  })
`.trim()}
      </CodeBlock>

      <Heading anchored id="authenticated-integrator-fetch" size={1} level={2}>
        <Code>authenticatedIntegratorFetch</Code>
      </Heading>

      <Text>
        The function can be used to make authenticated API calls to the
        Integrator's (your) API. Authorization will be sent with an
        Authorization "Bearer" header, e.g.{' '}
        <Code>
          Authorization: Bearer cn389ncoiwuencr664jgbrhrvhn43hcjdheg2j2f
        </Code>
        .
      </Text>

      <CodeBlock language="javascript">
        {`
authenticatedIntegratorFetch("https://api.example.com/some/endpoint")
  .then(({ data }) => {
    // do something with response data
  })
  .catch((err) => {
    console.error(err)
  })
}
`.trim()}
      </CodeBlock>

      <Heading anchored id="add-on-environment" size={1} level={2}>
        <Code>addOnEnvironment</Code>
      </Heading>

      <Text>
        This function returns either <Code>testing</Code>, <Code>beta</Code> or{' '}
        <Code>production</Code> depending on which environment your add-on code
        is running in. This can be useful for talking to different API endpoints
        in development vs in production.
      </Text>

      <CodeBlock language="javascript">
        {`
addOnEnvironment()
// => "testing"
`.trim()}
      </CodeBlock>

      <Heading anchored id="onRender" size={1} level={2}>
        <Code>onRender</Code>
      </Heading>

      <Text>
        This function is used to mount your add-on's React app. The boilerplate
        generated for you by the add-ons CLI tool should look like this:
      </Text>

      <CodeBlock language="javascript">
        {`
self.onRender((root, args) => {
  render(<App {...args} />, root, () => root.mount())
})
`.trim()}
      </CodeBlock>

      <Text>
        Additional <Code>args</Code> are passed into this callback, which you
        can in-turn pass to your root React component. The contents of{' '}
        <Code>args</Code> is determined by the insertion point.{' '}
        <Code>args</Code> will include data relevant to the insertion point such
        as <Code>currentPersonId</Code> or <Code>listId</Code> or whatever is
        useful.
      </Text>

      <Text>
        <Code>args</Code> will always contain the following:
      </Text>

      <List>
        <List.Item>
          <Text>
            <Code>location</Code>
          </Text>
          <List>
            <List.Item>
              <Text>
                <Code>protocol</Code>
              </Text>
            </List.Item>

            <List.Item>
              <Text>
                <Code>host</Code>
              </Text>
            </List.Item>

            <List.Item>
              <Text>
                <Code>pathname</Code>
              </Text>
            </List.Item>
          </List>
        </List.Item>
      </List>
    </StackView>
  )
}

export default page
